// Formula 1 World Championship Dataset Loader (1950-2020)
import Papa from 'papaparse';
import { applyDataEnhancements } from './f1DataEnhancer';
import { applyStatisticsCalculations } from './f1StatisticsCalculator';
import { applySeasonProcessing } from './f1SeasonProcessor';
import { filterF1Data, getF1ChampionshipStats } from './f1DataFilter';

/**
 * Load F1 World Championship dataset from Kaggle
 * @param {string} fileName - Specific file to load from the dataset
 * @returns {Promise} - Parsed F1 championship data
 */
export const loadF1ChampionshipData = async (fileName = 'races.csv') => {
  try {
    // Local path to F1 dataset in public/F1Data
    const baseUrl = '/datasets/F1Data';
    const url = `${baseUrl}/${fileName}`;
    
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const csvText = await response.text();
    
    return new Promise((resolve, reject) => {
      Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: true,
        complete: (results) => {
          resolve({
            name: fileName.replace('.csv', ''),
            data: results.data,
            url: url,
            loadedAt: new Date().toISOString()
          });
        },
        error: (error) => {
          reject(error);
        }
      });
    });
  } catch (error) {
    console.error('Error loading F1 championship data:', error);
    throw error;
  }
};

/**
 * Load all key F1 championship files
 * @returns {Promise} - Object with all F1 data
 */
export const loadAllF1ChampionshipData = async () => {
  try {
    const keyFiles = [
      'races.csv',           // Race information and results
      'results.csv',         // Race results and winners
      'qualifying.csv',      // Qualifying results for pole positions
      'circuits.csv',        // Circuit details
      'drivers.csv',         // Driver information
      'constructors.csv',    // Team/constructor information
      'seasons.csv',        // Season information
      'driver_standings.csv', // Championship standings by driver
      'constructor_standings.csv', // Championship standings by team
      'season_standings.csv', // Season-end standings by driver and year
      'status.csv'          // Status codes and descriptions
    ];

    const promises = keyFiles.map(async (file) => {
      const data = await loadF1ChampionshipData(file);
      return { file: file.replace('.csv', ''), data: data.data };
    });

    const results = await Promise.all(promises);

    let data = results.reduce((acc, { file, data }) => {
      acc[file] = data;
      return acc;
    }, {});

    // Apply data enhancements
    data = applyDataEnhancements(data);

    // Apply statistics calculations
    data = applyStatisticsCalculations(data);

    // Apply season processing
    data = applySeasonProcessing(data);

    return data;
  } catch (error) {
    console.error('Error loading all F1 championship data:', error);
    throw error;
  }
};

// Re-export functions from f1DataFilter for backward compatibility
export { getF1ChampionshipStats, filterF1Data } from './f1DataFilter';
